// components/promos/PromoShowcase.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useDragControls } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import Typography from "@mui/material/Typography";

const DIET_ICONS = Object.freeze({
    V: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/V_VG_GF%20icon/1.png",
    VG: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/V_VG_GF%20icon/2.png",
});

const DIET_LABELS = Object.freeze({
    V: "Vegetarian friendly",
    VG: "Vegan friendly"
});

/* ========= 数据：三个大类，每类 3 个 ========= */
const CATALOG = {
    "Healthy Bowls": [
        {
            title: "Vitality",
            calories: 740,
            desc:
                "Oregano salmon + Avocado, tri-quinoa and spinach mix, seasoned broccoli, beetroot,\n" +
                "kalamata olives, tomato and cucumber, red cabbage slaw, spiced chickpeas + Sicilian orange and rosemary vinaigrette, smoked aubergine with garlic",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/bowl%20pictures_square/Grain_-green_goddess_0000_Grain_Vitality.jpg",
            href: "/shop/bowls/vitality",
        },
        {
            title: "Keto Spartan",
            calories: 520,
            desc:
                "Herb grilled chicken + Avocado, leafy kale mix, kalamata olives, diced cucumber, red\n" +
                "cabbage slaw, spiced chickpeas, tomato and cucumber + Signature lemon and herb tahini, tzatziki",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/bowl%20pictures_square/Grain_-green_goddess_0004_Grain_keto_.jpg",
            href: "/shop/bowls/keto-spartan",
        },
        {
            title: "Green Goddess",
            calories: 480,
            desc:
                "Green falafel + Leafy spinach mix, diced cucumber, beetroot, spiced corn, tomato and red onion, pickled jalapeños + Zhoug, signature hummus",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/bowl%20pictures_square/Grain_-green_goddess_0006_.jpg",
            href: "/shop/bowls/green-goddess",
            diet: "VG",
        },
    ],
    Pitas: [
        {
            title: "Zeus’ Feast",
            calories: 680,
            desc:
                "Herb grilled chicken + Tomato and cucumber, kalamata olives, red cabbage slaw, pickled jalapeños + Yogurt dill, smokey red pepper hummus",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Pita_square/apollo__0000_zeus.jpg",
            href: "/shop/pitas/mediterranean",
        },
        {
            title: "Spartan Strength",
            calories: 750,
            desc:
                "Kleftiko lamb + Tomato and cucumber, red cabbage slaw, spiced chickpeas, seasoned broccoli + Greek tzatziki, hot harissa",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Pita_square/apollo__0001_spartan.jpg",
            href: "/shop/pitas/shawarma",
        },
        {
            title: "Apollo’s Garden",
            calories: 650,
            desc:
                "Green falafel + Beetroot, red cabbage slaw, tomato and cucumber, spiced corn + Zhoug, signature hummus\n",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Pita_square/apollo__0006_apollo.jpg",
            href: "/shop/pitas/halloumi",
            diet: "VG",
        },
    ],
    Breakfast: [
        {
            title: "The Deli Salt Beef",
            calories: 475,
            desc: "Tender salt beef + Pickled mayo + Ev olive oil dressing",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/salt_beef_pickle_white_0009.jpg",
            href: "/shop/breakfast/salmon-eggs",
        },
        {
            title: "Avocado Sunrise",
            calories: 445,
            desc: "Fresh smashed avocado, poached egg + Cream cheese + Ev olive oil dressing\n",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/egg_avo.jpg",
            href: "/shop/breakfast/avocado-crunch",
            diet: "V",
        },
        {
            title: "Aegean Smoked Salmon",
            price: "£6.50",
            calories: 325,
            weight: "135 g",
            protein: "15 g",
            carbs: "35 g",
            fat: "12 g",
            fibre: "2 g",
            desc: "Smoked salmon + Cream cheese + Lemon infused EV olive oil",
            image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/Grain_salmon_ccheese.png?x-oss-process=image/resize,m_fill,w_1200,h_1200",
            href: "/shop/breakfast/bagels-smoked-salmon",
        },
    ],
};

const CATEGORIES = ["Healthy Bowls", "Pitas", "Breakfast"];
const CAROUSEL_ID = "promo-showcase-carousel";
const slugify = (text = "") => text.replace(/\s+/g, "-").toLowerCase();

/* === 外层区块（包 tabs + 卡片网格） === */
const Section = styled.section`${tw`w-full text-dark-500 py-8`}`;

/* === 顶部 Tabs（Bowls / Pitas / Breakfast） === */
const TabsBar = styled.nav`
    ${tw`w-full`}
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 22px;
    padding: 16px 20px 0;
    font-family: "Roboto Condensed", sans-serif;

    @media (min-width: 768px) {
        padding: 24px 40px 0;
        gap: 28px;
    }
`;

const TabBtn = styled.button`
    ${tw`relative font-semibold`}
    background: transparent;
    border: 0;
    cursor: pointer;
    outline: none;
    padding: 10px 6px 18px;
    font-size: clamp(1.98rem, 1.92rem + 0.2vw, 2.12rem);
    line-height: 1;
    color: ${(p) => (p.$active ? "#14133B" : "#9CA3AF")};
    transition: color .12s ease;

    & > span {
        font-weight: 400;
        display: inline-block;
        transform-origin: center;
    }

    &::after {
        content: "";
        position: absolute;
        left: 50%;
        bottom: 4px;
        width: ${(p) => p.$dotSize ?? 8}px;
        height: ${(p) => p.$dotSize ?? 8}px;
        background: #14133B;
        border-radius: 50%;
        transform: translateX(-50%) scale(${(p) => (p.$active ? 1 : 0)});
        opacity: ${(p) => (p.$active ? 1 : 0)};
        transition: ${(p) =>
        p.$active ? "none" : "transform .18s ease, opacity .18s ease"};
    }

    &:hover::after {
        transform: translateX(-50%) scale(1);
        opacity: ${(p) => (p.$active ? 1 : 0.5)};
    }
`;

/* === 拖拽轮播容器 === */
const ShowcaseViewport = styled.div`
    position: relative;
    width: 100%;
    overflow: hidden;
`;

const ShowcaseWrap = styled(motion.div)`
    display: flex;
    touch-action: pan-y;
    will-change: transform;
    user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
`;

/* 关键：移动端为“100% - peek”让下一张露边；桌面保持 100% */
const Slide = styled.div`
    flex: 0 0 ${({ $isDesktop, $peek }) =>
        $isDesktop ? "100%" : `calc(100% - ${$peek || 0}px)`};
    width: ${({ $isDesktop, $peek }) =>
        $isDesktop ? "100%" : `calc(100% - ${$peek || 0}px)`};
    box-sizing: border-box;
    display: flex;
    padding: ${({ $isDesktop }) => ($isDesktop ? "0" : "20px 20px 78px 20px")};
    justify-content: ${({ $isDesktop }) => ($isDesktop ? "center" : "flex-start")};
`;

const DesktopGrid = styled.div`
    ${tw`w-full text-white`}
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
    padding: 28px 40px 162px;
`;

const MobileSlideInner = styled.div`
    ${tw`w-full text-white`}
    width: clamp(260px, 82vw, 360px);
    margin-right: 16px;
    scroll-snap-align: start;
`;

/* === 卡片 === */
const Tile = motion(styled.article`
    ${tw`relative`}
    overflow: hidden;
    border-radius: 20px;
    background: #fffbdc;
    cursor: grab;        /* 让用户知道可拖动 */
    &:active { cursor: grabbing; }
`);

const DietBadge = styled.img`
    position: absolute;
    top: 16px;
    right: 16px;
    width: clamp(28px, 26px + 1vw, 36px);
    height: clamp(28px, 26px + 1vw, 36px);
    pointer-events: none;
    z-index: 3;
`;

/* 图片比例盒：1:1；需要“下方圆角”，无阴影、无叠层 */
const RatioBox = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    overflow: hidden;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    @supports not (aspect-ratio: 1) { padding-top: 100%; }
`;

const TileImg = styled(motion.img)`
    position: absolute;
    inset: 0;
    width: 100%; height: 100%;
    object-fit: cover;
    display: block;
    will-change: transform;
    z-index: 0;
    -webkit-user-drag: none;  /* 禁用浏览器默认图片拖拽 */
    user-drag: none;
`;

/* 详情：左侧无 padding，与图片左边对齐 */
const Details = styled.div`
    position: relative;
    z-index: 1;
    color: #14133b;
    padding: 12px 12px 16px;

    @media (min-width: 768px) {
        padding: 14px 16px 18px;
    }

    .titleRow {
        display: flex;
        align-items: baseline;
        gap: 12px;
        flex-wrap: wrap;
    }

    .title {
        font-family: "Roboto", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
        font-weight: 300;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: #14133b;
        font-size: clamp(1.25rem, 1.02rem + 1vw, 1.6rem);
        line-height: 1.2;
        margin: 2px 0 6px;
    }

    .calories {
        margin-left: 10px;
        color: rgba(0, 0, 0, 0.5);
        font-size: 0.95rem;
        font-weight: 400;
        white-space: nowrap;
    }

    /* 仅桌面端（>=1024px）放大为 clamp */
    @media (min-width: 1024px) {
        .calories {
            font-size: clamp(1.25rem, 1.02rem + 1vw, 1.6rem);
        }
    }


    .desc {
        color: #14133b;
        font-size: clamp(1rem, 0.86rem + 0.2vw, 1.4rem);
        line-height: 1.35;
        text-wrap: pretty;
        margin-top: 6px;
    }
`;

/* “Order now >” 文本按钮：hover 下划线 */
const CtaText = styled.a`
    display: inline-block;
    margin-top: 20px;
    font-size: 1.2rem;
    font-weight: 300;
    color: #14133B;
    text-decoration: none;
    border-bottom: 2px solid transparent;
    transition: border-color .15s ease, transform .15s ease;

    &:hover { border-bottom-color: #14133B; }
    &:active { transform: translateY(1px); }
`;

/* 悬浮动效（禁用图片缩放） */
const hoverMotion = {
    whileHover: {},
    transition: { type: "spring", damping: 20, stiffness: 260 },
};
const imgVariants = {
    rest: { scale: 1 },
    hover: { scale: 1 },
};

export default function PromoShowcase({ initialCategory = "Healthy Bowls" }) {
    const MOBILE_PEEK = 56; // ✅ 手机端露出下一张的宽度（像素）

    const normalizedInitial = useMemo(() => {
        return CATEGORIES.includes(initialCategory) ? initialCategory : CATEGORIES[0];
    }, [initialCategory]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(0);
    const [isDesktop, setIsDesktop] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia("(min-width: 1024px)").matches;
    });

    const viewportRef = useRef(null);
    const dragControls = useDragControls(); // ✅ 统一从任意位置启动拖拽

    useEffect(() => {
        if (typeof window === "undefined") return;
        const media = window.matchMedia("(min-width: 1024px)");
        const handleChange = (event) => setIsDesktop(event.matches);

        if (media.addEventListener) media.addEventListener("change", handleChange);
        else media.addListener(handleChange);

        setIsDesktop(media.matches);
        return () => {
            if (media.removeEventListener) media.removeEventListener("change", handleChange);
            else media.removeListener(handleChange);
        };
    }, []);

    // 观测宽度
    useEffect(() => {
        const node = viewportRef.current;
        if (!node) return;

        const updateWidth = () => {
            const rect = node.getBoundingClientRect();
            setViewportWidth(rect.width);
        };

        updateWidth();
        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(updateWidth);
            observer.observe(node);
            return () => observer.disconnect();
        }
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, [isDesktop]);

    // ✅ 移动端 slide 宽度 = viewport - PEEK；桌面为 viewport
    const slideWidth = useMemo(() => {
        if (!viewportWidth) return 0;
        return isDesktop ? viewportWidth : Math.max(0, viewportWidth - MOBILE_PEEK);
    }, [viewportWidth, isDesktop]);

    // 构造 slides
    const slides = useMemo(() => {
        const collectItems = (category) =>
            (CATALOG[category] ?? []).map((item, idx) => ({
                ...item,
                category,
                _key: `${category}-${idx}`,
            }));

        if (isDesktop) {
            return CATEGORIES.map((category) => ({
                key: `slide-${category}`,
                category,
                items: collectItems(category),
            }));
        }

        const allSlides = [];
        CATEGORIES.forEach((category) => {
            collectItems(category).forEach((item) => {
                allSlides.push({
                    key: `slide-${item._key}`,
                    category,
                    items: [item],
                });
            });
        });
        return allSlides;
    }, [isDesktop]);

    const slideCount = slides.length;
    const canDrag = slideCount > 1 && slideWidth > 0;

    const lastCategoryRef = useRef(normalizedInitial);
    useEffect(() => { lastCategoryRef.current = normalizedInitial; }, [normalizedInitial]);

    const syncToIndex = useCallback((requestedIndex) => {
        if (!slideCount) return;
        const clamped = Math.max(0, Math.min(requestedIndex, slideCount - 1));
        setActiveIndex(clamped);
    }, [slideCount]);

    useEffect(() => {
        const current = slides[activeIndex]?.category;
        if (current) lastCategoryRef.current = current;
    }, [slides, activeIndex]);

    useEffect(() => {
        if (!slides.length) return;
        const targetCategory = lastCategoryRef.current;
        const targetIndex = slides.findIndex((s) => s.category === targetCategory);
        const fallbackIndex = targetIndex === -1 ? 0 : targetIndex;

        setActiveIndex((prev) => {
            if (slides[prev]) {
                const prevCategory = slides[prev].category;
                if (targetIndex === -1) return prev < slides.length ? prev : fallbackIndex;
                if (prevCategory === targetCategory) return prev;
            }
            return fallbackIndex;
        });
    }, [slides]);

    useEffect(() => {
        if (!slides.length) return;
        const targetIndex = slides.findIndex((s) => s.category === normalizedInitial);
        if (targetIndex === -1) return;
        lastCategoryRef.current = normalizedInitial;
        setActiveIndex((prev) => (prev === targetIndex ? prev : targetIndex));
    }, [normalizedInitial, slides]);

    const activeCategory =
        slides[activeIndex]?.category ?? lastCategoryRef.current ?? normalizedInitial;

    const handleTabClick = useCallback((category) => {
        const idx = slides.findIndex((slide) => slide.category === category);
        if (idx !== -1) {
            lastCategoryRef.current = category;
            syncToIndex(idx);
        }
    }, [slides, syncToIndex]);

    const handleDragStart = useCallback(() => {
        if (!canDrag) return;
        setIsDragging(true);
    }, [canDrag]);

    const handleDragEnd = useCallback((_, info) => {
        if (!canDrag) { setIsDragging(false); return; }
        const { offset, velocity } = info;
        const swipePower = offset.x + velocity.x * 0.2;
        const threshold = slideWidth * 0.2;

        let target = activeIndex;
        if (swipePower < -threshold) target = activeIndex + 1;
        else if (swipePower > threshold) target = activeIndex - 1;

        syncToIndex(target);
        setIsDragging(false);
    }, [canDrag, slideWidth, activeIndex, syncToIndex]);

    useEffect(() => {
        if (!slideCount) return;
        const idx = slides.findIndex((s) => s.category === activeCategory);
        if (idx === -1) return;
        setActiveIndex((prev) => (slides[prev]?.category === activeCategory ? prev : idx));
    }, [slides, slideCount, activeCategory]);

    useEffect(() => {
        if (!slideCount) return;
        if (!slides[activeIndex]) setActiveIndex(0);
    }, [slides, slideCount, activeIndex]);

    // ✅ 拖拽范围
    const dragConstraints = useMemo(() => {
        if (!canDrag) return { left: 0, right: 0 };
        return { left: -((slideCount - 1) * slideWidth), right: 0 };
    }, [canDrag, slideCount, slideWidth]);

    const animationX = canDrag ? -activeIndex * slideWidth : 0;
    const cursor = isDragging ? "grabbing" : canDrag ? "grab" : "default";

    // ✅ 在容器任意区域按下即启动拖拽（避免在交互元素上误触）
    const handlePointerDownCapture = useCallback((e) => {
        if (!canDrag) return;
        const target = e.target;
        const interactive = target.closest && target.closest("a, button, input, textarea, select");
        if (interactive) return; // 让交互元素保持点击行为
        dragControls.start(e);
    }, [canDrag, dragControls]);

    const renderTile = useCallback((item) => {
        const dietIcon = item.diet ? DIET_ICONS[item.diet] : undefined;
        const dietAlt = item.diet ? DIET_LABELS[item.diet] ?? `${item.diet} friendly` : undefined;

        return (
            <Tile
                key={item._key}
                {...hoverMotion}
                initial="rest"
                animate="rest"
                whileHover="hover"
            >
                {dietIcon && <DietBadge src={dietIcon} alt={dietAlt} loading="lazy" />}

                <RatioBox>
                    <TileImg
                        src={item.image}
                        alt={item.title || item._key}
                        loading="lazy"
                        variants={imgVariants}
                        transition={{ type: "spring", stiffness: 220, damping: 18 }}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                    />
                </RatioBox>

                <Details>
                    <div className="titleRow">
                        <Typography component="h3" className="title">
                            {item.title}
                        </Typography>
                    </div>

                    <p className="desc">{item.desc}</p>

                    <CtaText href="https://order.grainmediterranean.com/">
                        Order now &gt;
                    </CtaText>
                </Details>
            </Tile>
        );
    }, []);

    return (
        <Section aria-label="Promoted collections">
            <TabsBar role="tablist" aria-label="Categories">
                {CATEGORIES.map((cat) => {
                    const slug = slugify(cat);
                    const tabId = `tab-${slug}`;
                    const isActive = activeCategory === cat;
                    return (
                        <TabBtn
                            key={cat}
                            id={tabId}
                            $active={isActive}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={CAROUSEL_ID}
                            onClick={() => handleTabClick(cat)}
                        >
                            <span>{cat}</span>
                        </TabBtn>
                    );
                })}
            </TabsBar>

            <ShowcaseViewport
                id={CAROUSEL_ID}
                ref={viewportRef}
                role="group"
                aria-roledescription="carousel"
                aria-live="polite"
                onPointerDownCapture={handlePointerDownCapture}  // ✅ 任意位置启动拖拽
            >
                <ShowcaseWrap
                    key={`${slideCount}-${slideWidth}`}           // ✅ 约束变化时强制正确初始化
                    initial={false}
                    drag={canDrag ? "x" : false}
                    dragControls={dragControls}                   // ✅ 使用自定义 dragControls
                    dragListener={false}                          // ✅ 默认不监听，改为手动启动
                    dragConstraints={dragConstraints}
                    dragElastic={0.12}
                    dragMomentum={false}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    animate={{ x: animationX }}
                    transition={{ type: "spring", stiffness: 260, damping: 32 }}
                    style={{ cursor }}
                    $isDesktop={isDesktop}
                >
                    {slides.map((slide, idx) => {
                        const tabId = `tab-${slugify(slide.category)}`;
                        const [firstItem] = slide.items;

                        return (
                            <Slide
                                key={slide.key}
                                $isDesktop={isDesktop}
                                $peek={MOBILE_PEEK}                     // ✅ 移动端露出下一张的宽度
                                aria-hidden={idx !== activeIndex}
                                role="group"
                                aria-labelledby={tabId}
                            >
                                {isDesktop ? (
                                    <DesktopGrid>
                                        {slide.items.map((item) => renderTile(item))}
                                    </DesktopGrid>
                                ) : (
                                    <MobileSlideInner>
                                        {firstItem ? renderTile(firstItem) : null}
                                    </MobileSlideInner>
                                )}
                            </Slide>
                        );
                    })}
                </ShowcaseWrap>
            </ShowcaseViewport>
        </Section>
    );
}
